import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Gauge, Package, Timer, AlertTriangle, PauseCircle, Euro, Target,
  TrendingUp, TrendingDown, Settings2, Trash2, Loader2, Wrench, X, Plus, Award, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { HelpHint } from "@/components/HelpHint";
import { Modal, StopModal, ScrapModal } from "@/components/ProductionEntry";

const euro = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);
const num = (n) => new Intl.NumberFormat("it-IT").format(Math.round(n || 0));
const GREEN = "#2e7d32", AMBER = "#b45309", RED = "#9c1e2b", BLUE = "#33559d";
const pctColor = (pct) => (pct >= 100 ? GREEN : pct >= 85 ? AMBER : RED);
const oeeColor = (oee, tgt) => (tgt ? pctColor((oee / tgt) * 100) : oee >= 75 ? GREEN : oee >= 60 ? AMBER : RED);
const scrapColor = (v, max) => (!max ? BLUE : v <= max ? GREEN : v <= max * 1.5 ? AMBER : RED);
const today = () => new Date().toISOString().slice(0, 10);
const curMonth = () => new Date().toISOString().slice(0, 7);

function Bar({ pct, color }) {
  return (
    <div className="h-2 rounded-full bg-[#e9edf4] overflow-hidden">
      <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, pct)}%`, background: color }} />
    </div>
  );
}

function Trend({ cur, prev, invert }) {
  if (prev === undefined || prev === null) return null;
  const diff = cur - prev;
  if (Math.abs(diff) < 0.01) return <span className="text-[11px] t-muted">= prec.</span>;
  const up = diff > 0;
  const good = invert ? !up : up;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span className="text-[11px] inline-flex items-center gap-0.5" style={{ color: good ? GREEN : RED }}>
      <Icon size={12} /> {up ? "+" : ""}{Math.round(diff * 10) / 10}
    </span>
  );
}

function KpiCard({ testid, icon: Icon, label, accent, onClick, help, children }) {
  return (
    <div data-testid={testid} onClick={onClick} role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick && onClick(); } }}
      className="panel rounded-[4px] p-3.5 text-left flex flex-col gap-1.5 min-h-[118px] hover:shadow-md transition-shadow cursor-pointer" style={{ borderTop: `3px solid ${accent}` }}>
      <div className="flex items-center justify-between">
        <span className="lbl inline-flex items-center gap-1">{label}{help && <span onClick={(e) => e.stopPropagation()}>{help}</span>}</span>
        <Icon size={16} style={{ color: accent }} strokeWidth={1.9} />
      </div>
      {children}
    </div>
  );
}

const KPI_HELP = {
  pieces: "Macchinari prodotti = macchine da anagrafica (con scheda) CONSEGNATE nel periodo, contate solo per le macchine/ordini messi a target. I componenti inseriti a mano non contano. Denominatore = target pezzi (somma dei target per macchina + macchine degli ordini da consegnare). % = prodotte ÷ target × 100. Esempio: target 27, consegnate 5 → 5/27 = 19%.",
  points: "Punteggio incentivi = somma di (punteggio della macchina × quantità prodotta nel periodo). Il punteggio di ogni macchina si imposta in Anagrafica Macchine. Denominatore = target punti del mese. Esempio: Decanter (punteggio 6) × 2 + Frangitore (4) × 1 = 12 + 4 = 16 punti; con target 94 → 17%.",
  cycle: "Tempo ciclo medio = ore REALI registrate su tutte le fasi delle macchine consegnate nel periodo ÷ numero di macchine consegnate. Lo 'std' è lo stesso calcolo ma con le ore di riferimento da anagrafica. Il delta (reale − std) in verde = più veloci dello standard, in rosso = più lenti. Esempio: 3 macchine, 207 h reali totali → 69 h/pz; standard 275,5 h/pz → −206,5 h (più veloci).",
  scrap: "% Scarti/difetti = pezzi scartati ÷ (pezzi buoni consegnati + pezzi scartati) × 100. Gli scarti si registrano col pulsante 'Scarto'. La freccia confronta col mese precedente (giù = miglioramento). Esempio: 2 pezzi scartati e 48 buoni → 2 / 50 = 4%.",
  downtime: "Fermi macchina = somma dei minuti di TUTTI i fermi registrati nel periodo (pulsante 'Fermo macchina'), convertiti in ore (÷ 60). Se impostato un costo, viene sommato anche il costo riparazioni. Esempio: un fermo da 90 min + uno da 30 min = 120 min = 2 h.",
  value: "Valore produzione = somma del valore delle macchine CONSEGNATE nel periodo. Il valore di ogni macchina = prezzo dell'ordine se indicato, altrimenti il prezzo di listino da anagrafica. 'target' = obiettivo € del mese; 'proiez.' = valore attuale ÷ frazione di mese già trascorsa (stima a fine mese). Esempio: a metà mese 291.916 € → proiezione ≈ 603.293 €.",
};


export default function ProductionKpis({ compact = false }) {
  const { user } = useAuth();
  const canEdit = ["management", "admin", "amministrazione"].includes(user.role);
  const isEcon = ["management", "admin", "amministrazione"].includes(user.role);
  const [f, setF] = useState({ month: curMonth(), range: "month", machine: "all" });
  const [d, setD] = useState(null);
  const [stops, setStops] = useState([]);
  const [scraps, setScraps] = useState([]);
  const [modal, setModal] = useState(null); // 'stop'|'scrap'|'target'|'detail'
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    try {
      const p = new URLSearchParams({ month: f.month, range: f.range, machine: f.machine });
      const { data } = await api.get(`/production/analytics?${p.toString()}`);
      setD(data);
    } catch (e) { toast.error("Errore caricamento KPI"); }
  }, [f]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);
  const loadLists = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([api.get("/production/stops"), api.get("/production/scrap")]);
      setStops(a.data); setScraps(b.data);
    } catch (e) {}
  }, []);
  useEffect(() => { loadLists(); }, [loadLists]);

  if (!d) return <div className="panel rounded-[4px] p-6 t-muted">Caricamento KPI produzione…</div>;
  const c = d.current, mw = d.month_window, t = d.targets, pr = d.projection;
  const piecesDone = t.pieces_done ?? mw.pieces;
  const piecesPct = t.pieces ? Math.round((piecesDone / t.pieces) * 100) : (piecesDone ? 100 : 0);
  const pointsDone = t.points_done ?? 0;
  const pointsPct = t.points ? Math.round((pointsDone / t.points) * 100) : (pointsDone ? 100 : 0);
  const valuePct = t.value ? Math.round((mw.value / t.value) * 100) : (mw.value ? 100 : 0);
  const cycleDelta = Math.round((c.cycle_actual_h - c.cycle_std_h) * 100) / 100;

  const openDetail = (kind) => { setDetail(kind); setModal("detail"); };

  return (
    <div data-testid="production-kpis" className="mb-8">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="flex items-center gap-1.5 mr-auto">
          <Gauge size={18} className="text-[#33559d]" />
          <h2 className="font-display font-700 text-lg t-head">Produzione — KPI &amp; Target</h2>
        </div>
        <input data-testid="kpi-filter-month" type="month" value={f.month} onChange={(e) => setF({ ...f, month: e.target.value })} className="field py-1.5 text-sm w-[140px]" />
        <select data-testid="kpi-filter-range" value={f.range} onChange={(e) => setF({ ...f, range: e.target.value })} className="field py-1.5 text-sm">
          <option value="month">Mese</option><option value="week">Ultimi 7 giorni</option><option value="day">Oggi</option>
        </select>
        <select data-testid="kpi-filter-machine" value={f.machine} onChange={(e) => setF({ ...f, machine: e.target.value })} className="field py-1.5 text-sm max-w-[220px]">
          <option value="all">Tutte le macchine</option>
          {d.machines.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        {compact ? <Link data-testid="kpi-open-management" to="/gestione-produzione" className="text-sm font-600 text-[#33559d] hover:underline">Gestisci in Produzione e target →</Link> : <>
        <button data-testid="btn-add-stop" onClick={() => setModal("stop")} className="btn-industrial inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[3px] text-sm bg-[#9c1e2b] text-white"><Wrench size={14} /> Fermo macchina</button>
        <button data-testid="btn-add-scrap" onClick={() => setModal("scrap")} className="btn-industrial inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[3px] text-sm bg-[#b45309] text-white"><AlertTriangle size={14} /> Scarto</button>
        {canEdit && <button data-testid="btn-set-target" onClick={() => setModal("target")} className="btn-industrial inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[3px] text-sm bg-[#33559d] text-white"><Settings2 size={14} /> Target</button>}
        </>}
      </div>

      {/* KPI header (scorre con la pagina) */}
      <div className="-mx-1 px-1 py-1 rounded-[4px]">
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-2.5">
          {/* Pieces */}
          <KpiCard testid="kpi-pieces" icon={Package} label="Macchinari prodotti" accent={pctColor(piecesPct)} onClick={() => openDetail("pieces")} help={<HelpHint testid="help-pieces" text={KPI_HELP.pieces} />}>
            <div className="font-display font-700 text-3xl leading-none t-head">{num(piecesDone)}<span className="text-base t-muted"> / {num(t.pieces)}</span></div>
            <div className="mt-1"><Bar pct={piecesPct} color={pctColor(piecesPct)} /></div>
            <div className="text-[10px] t-faint">{piecesPct}% del target</div>
          </KpiCard>
          {/* Punteggio incentivi */}
          <KpiCard testid="kpi-points" icon={Award} label="Punteggio" accent={pctColor(pointsPct)} onClick={() => openDetail("points")} help={<HelpHint testid="help-points" text={KPI_HELP.points} />}>
            <div className="font-display font-700 text-3xl leading-none text-[#6b3fa0]">{num(pointsDone)}<span className="text-base t-muted"> / {num(t.points)}</span></div>
            <div className="mt-1"><Bar pct={pointsPct} color={pctColor(pointsPct)} /></div>
            <div className="text-[10px] t-faint">{pointsPct}% del target punti</div>
          </KpiCard>
          {/* Cycle time */}
          <KpiCard testid="kpi-cycle" icon={Timer} label="Tempo ciclo medio" accent={cycleDelta > 0 ? RED : GREEN} onClick={() => openDetail("cycle")} help={<HelpHint testid="help-cycle" text={KPI_HELP.cycle} />}>
            <div className="font-display font-700 text-3xl leading-none t-head">{c.cycle_actual_h}<span className="text-base t-muted"> h/pz</span></div>
            <div className="text-[11px]" style={{ color: cycleDelta > 0 ? RED : GREEN }}>
              {cycleDelta > 0 ? "+" : ""}{cycleDelta} h vs std {c.cycle_std_h}h
            </div>
          </KpiCard>
          {/* Scrap */}
          <KpiCard testid="kpi-scrap" icon={AlertTriangle} label="% Scarti / difetti" accent={scrapColor(c.scrap_pct, t.scrap_max)} onClick={() => openDetail("scrap")} help={<HelpHint testid="help-scrap" text={KPI_HELP.scrap} />}>
            <div className="font-display font-700 text-3xl leading-none" style={{ color: scrapColor(c.scrap_pct, t.scrap_max) }}>{c.scrap_pct}%</div>
            <div className="flex items-center gap-2 text-[10px] t-muted">
              {!!t.scrap_max && <span>max {t.scrap_max}%</span>}
              <Trend cur={c.scrap_pct} prev={d.prev_month?.scrap_pct} invert />
            </div>
            <div className="text-[10px] t-faint">{c.scrap_qty} pz scartati</div>
          </KpiCard>
          {/* Downtime */}
          <KpiCard testid="kpi-downtime" icon={PauseCircle} label="Fermi macchina" accent={c.downtime_h > 0 ? AMBER : GREEN} onClick={() => openDetail("downtime")} help={<HelpHint testid="help-downtime" text={KPI_HELP.downtime} align="right" />}>
            <div className="font-display font-700 text-3xl leading-none t-head">{c.downtime_h}<span className="text-base t-muted"> h</span></div>
            <div className="text-[11px] t-muted truncate">{c.stop_by_cause[0] ? `${c.stop_by_cause[0].cause}` : "Nessun fermo"}</div>
            {!!c.downtime_cost && <div className="text-[10px] t-faint">costi {euro(c.downtime_cost)}</div>}
          </KpiCard>
          {/* Value — economic roles only */}
          {isEcon && d.show_value && (
          <KpiCard testid="kpi-value" icon={Euro} label="Valore produzione" accent={pctColor(valuePct)} onClick={() => openDetail("value")} help={<HelpHint testid="help-value" text={KPI_HELP.value} align="right" />}>
            <div className="font-display font-700 text-2xl leading-none t-head">{euro(mw.value)}</div>
            <div className="mt-1"><Bar pct={valuePct} color={pctColor(valuePct)} /></div>
            <div className="text-[10px] t-faint">target {euro(t.value)} · proiez. {euro(pr.value)}</div>
          </KpiCard>
          )}
        </div>
      </div>

      {/* Target Mensili panel */}
      {!compact && <div data-testid="target-panel" className="panel rounded-[4px] p-4 lg:p-5 mt-4">
        <div className="flex items-center gap-2 mb-3 pb-2 border-b hairline">
          <Target size={16} className="text-[#33559d]" />
          <h3 className="font-display font-700 t-head">Target Mensili · {d.month}</h3>
          {d.is_current_month && <span className="text-[11px] t-muted ml-auto">Proiezione a fine mese in base al ritmo attuale</span>}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-3">
          {d.lines_progress.map((lp) => {
            const proj = d.is_current_month && d.elapsed_fraction > 0 ? Math.round(lp.real / d.elapsed_fraction) : lp.real;
            const col = pctColor(lp.pct);
            return (
              <div key={lp.line} data-testid={`target-line-${lp.line}`}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="font-600 t-head">{lp.line}</span>
                  <span className="t-muted">{lp.real} / {lp.target || "—"} pz <span className="font-600" style={{ color: col }}>({lp.pct}%)</span></span>
                </div>
                <Bar pct={lp.pct} color={col} />
                {lp.orders && lp.orders.length > 0 && (
                  <div className="text-[10px] t-muted mt-0.5" data-testid={`target-orders-${lp.line}`}>
                    <span className="t-faint">Ordini:</span>{" "}
                    {(lp.orders_detail || lp.orders.map((c) => ({ code: c, delivered: false }))).map((o, oi, arr) => (
                      <span key={o.code} data-testid={`target-order-${lp.line}-${o.code}`}
                        className={o.delivered ? "text-[#1c7a44] font-700" : ""}
                        title={o.delivered ? "Consegnato" : "Da consegnare"}>
                        {o.code}{oi < arr.length - 1 ? ", " : ""}
                      </span>
                    ))}
                  </div>
                )}
                {!!lp.target && <div className="text-[10px] t-faint mt-0.5">Proiezione fine mese: {proj} pz</div>}
              </div>
            );
          })}
        </div>
        {/* aggregate targets + comparisons */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4 pt-3 border-t hairline">
          {[
            { k: "OEE", real: `${mw.oee}%`, tgt: t.oee ? `${t.oee}%` : "—", col: oeeColor(mw.oee, t.oee), pm: d.prev_month?.oee, py: d.prev_year?.oee, cur: mw.oee, suf: "%" },
            ...((isEcon && d.show_value) ? [{ k: "Valore", real: euro(mw.value), tgt: t.value ? euro(t.value) : "—", col: pctColor(valuePct), pm: d.prev_month?.value, py: d.prev_year?.value, cur: mw.value, suf: "€", money: true }] : []),
            { k: "Scarti", real: `${mw.scrap_pct}%`, tgt: t.scrap_max ? `≤ ${t.scrap_max}%` : "—", col: scrapColor(mw.scrap_pct, t.scrap_max), pm: d.prev_month?.scrap_pct, py: d.prev_year?.scrap_pct, cur: mw.scrap_pct, suf: "%", invert: true },
          ].map((x) => (
            <div key={x.k} className="rounded-[3px] border hairline p-2.5">
              <div className="lbl mb-0.5">{x.k}</div>
              <div className="flex items-baseline gap-2">
                <span className="font-display font-700 text-xl" style={{ color: x.col }}>{x.real}</span>
                <span className="text-[11px] t-muted">target {x.tgt}</span>
              </div>
              <div className="flex items-center gap-3 mt-1 text-[10px] t-muted">
                <span>Mese prec.: {x.money ? euro(x.pm) : `${x.pm ?? 0}${x.suf}`}</span>
                <span>Anno prec.: {x.money ? euro(x.py) : `${x.py ?? 0}${x.suf}`}</span>
              </div>
            </div>
          ))}
        </div>
      </div>}

      {modal === "stop" && <StopModal onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); loadLists(); }} />}
      {modal === "scrap" && <ScrapModal onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); loadLists(); }} />}
      {modal === "target" && <TargetModal month={f.month} machines={Object.keys(d.machine_scores || {}).sort((a, b) => a.localeCompare(b))} machineScores={d.machine_scores || {}} isEcon={isEcon && d.show_value} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {modal === "detail" && <DetailModal kind={detail} d={d} stops={stops} scraps={scraps} canEdit={canEdit}
        onClose={() => setModal(null)} onDelStop={async (id) => { await api.delete(`/production/stops/${id}`); loadLists(); load(); }}
        onDelScrap={async (id) => { await api.delete(`/production/scrap/${id}`); loadLists(); load(); }} />}
    </div>
  );
}

function TargetModal({ month, machines, machineScores = {}, isEcon, onClose, onSaved }) {
  const [m, setM] = useState(month);
  const [rows, setRows] = useState([]); // [{line, qty}]
  const [pick, setPick] = useState("");
  const [oee, setOee] = useState(0);
  const [scrapMax, setScrapMax] = useState(0);
  const [points, setPoints] = useState(0);
  const [value, setValue] = useState(0);
  const [saving, setSaving] = useState(false);
  const [orderList, setOrderList] = useState([]); // [{code, client, final_customer}]
  const [orders, setOrders] = useState([]); // [{code, client, final_customer, machines:[{id,product}], machine_ids:[]}]
  const [orderPick, setOrderPick] = useState("");

  const calcPoints = (rowsArr, ordersArr) => {
    let s = 0;
    for (const r of rowsArr) s += (machineScores[r.line] || 0) * (parseFloat(r.qty) || 0);
    for (const o of ordersArr) for (const mm of o.machines) if (o.machine_ids.includes(mm.id)) s += (machineScores[mm.product] || 0);
    return Math.round(s * 100) / 100;
  };
  const suggestedPoints = calcPoints(rows, orders);

  useEffect(() => {
    api.get("/orders").then(({ data }) => setOrderList((data || []).map((o) => ({ code: o.code, client: o.client, final_customer: o.final_customer })))).catch(() => {});
  }, []);
  useEffect(() => {
    api.get(`/production/targets?month=${m}`).then(async ({ data }) => {
      setRows((data.lines || []).filter((x) => x.line).map((x) => ({ line: x.line, qty: x.qty || 0 })));
      setOee(data.target_oee || 0); setScrapMax(data.target_scrap_max || 0); setPoints(data.target_points || 0); setValue(data.target_value || 0);
      const saved = data.orders || [];
      const loaded = await Promise.all(saved.map(async (t) => {
        try {
          const { data: od } = await api.get(`/orders/${encodeURIComponent(t.code)}`);
          const macs = (od.machines || []).map((mm) => ({ id: mm.id, product: mm.product }));
          const ids = (t.machine_ids && t.machine_ids.length) ? t.machine_ids : macs.map((mm) => mm.id);
          return { code: t.code, client: od.client, final_customer: od.final_customer, machines: macs, machine_ids: ids };
        } catch { return null; }
      }));
      setOrders(loaded.filter(Boolean));
    });
  }, [m]);
  const addRow = () => {
    if (!pick || rows.some((r) => r.line === pick)) return;
    const next = [...rows, { line: pick, qty: 0 }];
    setRows(next); setPick(""); setPoints(calcPoints(next, orders));
  };
  const setRowQty = (i, qty) => {
    const next = rows.map((x, j) => (j === i ? { ...x, qty } : x));
    setRows(next); setPoints(calcPoints(next, orders));
  };
  const delRow = (i) => {
    const next = rows.filter((_, j) => j !== i);
    setRows(next); setPoints(calcPoints(next, orders));
  };
  const addOrder = async () => {
    if (!orderPick || orders.some((o) => o.code === orderPick)) { setOrderPick(""); return; }
    try {
      const { data: od } = await api.get(`/orders/${encodeURIComponent(orderPick)}`);
      const macs = (od.machines || []).map((mm) => ({ id: mm.id, product: mm.product }));
      const next = [...orders, { code: orderPick, client: od.client, final_customer: od.final_customer, machines: macs, machine_ids: macs.map((mm) => mm.id) }];
      setOrders(next); setOrderPick(""); setPoints(calcPoints(rows, next));
    } catch { toast.error("Errore caricamento ordine"); }
  };
  const delOrder = (code) => {
    const next = orders.filter((x) => x.code !== code);
    setOrders(next); setPoints(calcPoints(rows, next));
  };
  const toggleMac = (code, id) => {
    const next = orders.map((o) => o.code === code
      ? { ...o, machine_ids: o.machine_ids.includes(id) ? o.machine_ids.filter((x) => x !== id) : [...o.machine_ids, id] }
      : o);
    setOrders(next); setPoints(calcPoints(rows, next));
  };
  const orderMacCount = orders.reduce((s, o) => s + o.machine_ids.length, 0);
  const save = async () => {
    setSaving(true);
    try {
      const orderPayload = orders
        .filter((o) => o.machine_ids.length > 0)
        .map((o) => ({ code: o.code, machine_ids: o.machine_ids.length === o.machines.length ? [] : o.machine_ids }));
      const payload = { month: m, lines: rows, orders: orderPayload, target_oee: parseFloat(oee) || 0, target_scrap_max: parseFloat(scrapMax) || 0, target_points: parseFloat(points) || 0 };
      if (isEcon) payload.target_value = parseFloat(value) || 0;
      await api.put("/production/targets", payload);
      toast.success("Target salvati"); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } finally { setSaving(false); }
  };
  const available = machines.filter((mac) => !rows.some((r) => r.line === mac));
  const availOrders = orderList.filter((o) => !orders.some((x) => x.code === o.code));
  return (
    <Modal title="Imposta target mensili" testid="target-modal" wide onClose={onClose}>
      <div className="space-y-3">
        <div><label className="lbl">Mese</label><input data-testid="target-month" type="month" value={m} onChange={(e) => setM(e.target.value)} className="field w-[160px]" /></div>
        <div>
          <label className="lbl">Target pezzi per macchina</label>
          <div className="space-y-1.5 mt-1 max-h-[220px] overflow-y-auto pr-1">
            {rows.map((r, i) => (
              <div key={r.line} className="flex items-center gap-2">
                <span className="text-sm t-body flex-1 truncate" title={r.line}>{r.line}</span>
                <input data-testid={`target-qty-${r.line}`} type="number" value={r.qty} onChange={(e) => setRowQty(i, e.target.value)} className="field w-20 py-1 text-sm text-center" />
                <button data-testid={`target-row-del-${r.line}`} onClick={() => delRow(i)} className="text-[#9c1e2b] p-1"><X size={13} /></button>
              </div>
            ))}
            {rows.length === 0 && <div className="text-[11px] t-faint">Aggiungi le macchine per cui vuoi fissare un obiettivo di pezzi.</div>}
          </div>
          <div className="flex items-center gap-2 mt-2">
            <select data-testid="target-machine-pick" value={pick} onChange={(e) => setPick(e.target.value)} className="field flex-1 py-1.5 text-sm">
              <option value="">Scegli una macchina…</option>
              {available.map((mac) => <option key={mac} value={mac}>{mac}</option>)}
            </select>
            <button data-testid="target-machine-add" onClick={addRow} disabled={!pick} className="btn-industrial inline-flex items-center gap-1 px-3 py-1.5 rounded-[3px] text-sm bg-[#33559d] text-white disabled:opacity-50"><Plus size={14} /> Aggiungi</button>
          </div>
        </div>

        {/* Target per ordine */}
        <div className="pt-2 border-t hairline">
          <label className="lbl">Target per ordine da consegnare {orderMacCount > 0 && <span className="t-muted normal-case">· +{orderMacCount} macchine al target</span>}</label>
          <div className="space-y-2 mt-1 max-h-[240px] overflow-y-auto pr-1">
            {orders.map((o) => (
              <div key={o.code} data-testid={`target-order-${o.code}`} className="rounded-[3px] border hairline p-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-600 t-head truncate" title={`${o.client || ""}${o.final_customer ? " · Cliente finale: " + o.final_customer : ""}`}>Ordine {o.code} <span className="t-muted font-400">· {o.client || "—"}</span>{o.final_customer ? <span className="t-faint font-400"> → {o.final_customer}</span> : ""}</span>
                  <button data-testid={`target-order-del-${o.code}`} onClick={() => delOrder(o.code)} className="text-[#9c1e2b] p-1 shrink-0"><X size={13} /></button>
                </div>
                <div className="space-y-1">
                  {o.machines.map((mm) => (
                    <label key={mm.id} className="flex items-center gap-2 text-[13px] t-body cursor-pointer">
                      <input data-testid={`target-order-${o.code}-mac-${mm.id}`} type="checkbox" checked={o.machine_ids.includes(mm.id)} onChange={() => toggleMac(o.code, mm.id)} className="accent-[#33559d]" />
                      <span className="truncate">{mm.product || "—"}</span>
                    </label>
                  ))}
                  {o.machines.length === 0 && <div className="text-[11px] t-faint">Nessuna macchina nell'ordine.</div>}
                </div>
              </div>
            ))}
            {orders.length === 0 && <div className="text-[11px] t-faint">Aggiungi gli ordini da consegnare questo mese: le macchine selezionate si sommano al target pezzi.</div>}
          </div>
          <div className="flex items-center gap-2 mt-2">
            <select data-testid="target-order-pick" value={orderPick} onChange={(e) => setOrderPick(e.target.value)} className="field flex-1 py-1.5 text-sm">
              <option value="">Scegli un ordine…</option>
              {availOrders.map((o) => <option key={o.code} value={o.code}>{o.code} — {o.client || "—"}{o.final_customer ? ` → ${o.final_customer}` : ""}</option>)}
            </select>
            <button data-testid="target-order-add" onClick={addOrder} disabled={!orderPick} className="btn-industrial inline-flex items-center gap-1 px-3 py-1.5 rounded-[3px] text-sm bg-[#33559d] text-white disabled:opacity-50"><Plus size={14} /> Aggiungi</button>
          </div>
        </div>

        <div className={`grid ${isEcon ? "grid-cols-3" : "grid-cols-2"} gap-3 pt-2 border-t hairline items-start`}>
          <div className="flex flex-col">
            <label className="lbl leading-tight min-h-[38px] flex items-end">Scarti max %</label>
            <input data-testid="target-scrap" type="number" value={scrapMax} onChange={(e) => setScrapMax(e.target.value)} className="field w-full" />
          </div>
          <div className="flex flex-col">
            <label className="lbl leading-tight min-h-[38px] flex items-end">Target punti mese</label>
            <div className="flex items-center gap-1.5">
              <input data-testid="target-points" type="number" step="0.1" value={points} onChange={(e) => setPoints(e.target.value)} className="field w-full" />
              <button type="button" data-testid="target-points-refresh" title="Aggiorna con il valore calcolato dalle macchine/ordini a target"
                onClick={() => setPoints(suggestedPoints)}
                className="btn-industrial shrink-0 inline-flex items-center justify-center p-2 rounded-[3px] border hairline text-[#33559d] hover:bg-[#eef2fb]">
                <RefreshCw size={14} />
              </button>
            </div>
            <span data-testid="target-points-suggested" className="text-[10px] t-faint mt-0.5 leading-tight">
              Calcolato dai target: <b className="t-muted">{suggestedPoints}</b> pt
            </span>
          </div>
          {isEcon && (
            <div className="flex flex-col">
              <label className="lbl leading-tight min-h-[38px] flex items-end">Valore € mese</label>
              <input data-testid="target-value" type="number" value={value} onChange={(e) => setValue(e.target.value)} className="field w-full" />
            </div>
          )}
        </div>
        <button data-testid="target-save" onClick={save} disabled={saving} className="btn-industrial w-full py-2.5 rounded-[3px] bg-[#33559d] text-white inline-flex items-center justify-center gap-2 disabled:opacity-60">{saving ? <Loader2 size={15} className="animate-spin" /> : <Target size={15} />} Salva target</button>
      </div>
    </Modal>
  );
}

function StatRow({ label, cur, pm, py, suf = "", money }) {
  const fmt = (v) => (money ? euro(v) : `${v ?? 0}${suf}`);
  return (
    <div className="flex items-center justify-between text-sm py-1.5 border-b hairline last:border-0">
      <span className="t-body">{label}</span>
      <span className="flex items-center gap-4">
        <span className="font-600 t-head">{fmt(cur)}</span>
        <span className="text-[11px] t-muted w-28 text-right">prec. {fmt(pm)} · anno {fmt(py)}</span>
      </span>
    </div>
  );
}

function DetailModal({ kind, d, stops, scraps, canEdit, onClose, onDelStop, onDelScrap }) {
  const c = d.current, pm = d.prev_month, py = d.prev_year;
  const titles = { oee: "OEE — dettaglio", pieces: "Macchinari prodotti — dettaglio", points: "Punteggio — dettaglio", cycle: "Tempo ciclo — dettaglio", scrap: "Scarti / difetti — dettaglio", downtime: "Fermi macchina — dettaglio", value: "Valore produzione — dettaglio" };
  const helpText = { ...KPI_HELP, oee: "OEE = Disponibilità × Performance × Qualità. Disponibilità = ore lavorate ÷ (ore lavorate + ore di fermo). Performance = ore di riferimento ÷ ore reali (max 100%). Qualità = pezzi buoni ÷ (buoni + scartati). Esempio: 90% × 85% × 96% ≈ 73%." };
  const modalTitle = <span className="inline-flex items-center gap-1.5">{titles[kind]}{helpText[kind] && <HelpHint testid={`help-detail-${kind}`} text={helpText[kind]} />}</span>;
  const monthStops = stops.filter((s) => (s.date || "").slice(0, 7) === d.month && (d.machine === "all" || s.machine === d.machine || s.line === d.machine));
  const monthScraps = scraps.filter((s) => (s.date || "").slice(0, 7) === d.month && (d.machine === "all" || s.product === d.machine || s.line === d.machine));
  return (
    <Modal title={modalTitle} testid="kpi-detail-modal" wide onClose={onClose}>
      {kind === "oee" && (
        <div className="space-y-2">
          <div className="grid grid-cols-4 gap-2 text-center">
            {[["OEE", c.oee], ["Disponibilità", c.availability], ["Performance", c.performance], ["Qualità", c.quality]].map(([l, v]) => (
              <div key={l} className="rounded-[3px] border hairline p-2"><div className="lbl">{l}</div><div className="font-display font-700 text-xl text-[#33559d]">{v}%</div></div>
            ))}
          </div>
          <p className="text-[11px] t-muted">OEE = Disponibilità × Performance × Qualità, calcolato da ore lavorate, fermi macchina e scarti inseriti.</p>
          <StatRow label="OEE nel periodo" cur={c.oee} pm={pm.oee} py={py.oee} suf="%" />
        </div>
      )}
      {kind === "pieces" && (
        <div className="space-y-1">
          {d.lines_progress.map((lp) => (
            <div key={lp.line} className="flex items-center justify-between text-sm py-1.5 border-b hairline">
              <span className="t-body">{lp.line}</span><span className="font-600 t-head">{lp.real} / {lp.target || "—"} <span className="text-[11px]" style={{ color: pctColor(lp.pct) }}>({lp.pct}%)</span></span>
            </div>
          ))}
          <StatRow label="Macchinari da anagrafica (a target)" cur={d.targets.pieces_done ?? d.month_window.pieces_catalog} pm={pm.pieces_catalog ?? pm.pieces} py={py.pieces_catalog ?? py.pieces} />
          <StatRow label="Macchinari da anagrafica (totale)" cur={d.month_window.pieces_catalog ?? d.month_window.pieces} pm={pm.pieces_catalog ?? pm.pieces} py={py.pieces_catalog ?? py.pieces} />
          <StatRow label="Totale ordini consegnati (incl. componenti manuali)" cur={d.month_window.pieces} pm={pm.pieces} py={py.pieces} />
        </div>
      )}
      {kind === "points" && (
        <div className="space-y-2">
          <div className="flex items-center justify-between rounded-[3px] bg-[#f3eefb] border border-[#e0d4f2] px-3 py-2">
            <span className="text-sm t-body">Totale punti conseguiti</span>
            <span className="font-display font-700 text-lg text-[#6b3fa0]">{num(d.targets.points_done)} / {num(d.targets.points)}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="lbl text-left border-b hairline">
                <th className="py-1.5 pr-2">Macchina</th>
                <th className="py-1.5 px-2 text-center">Q.tà mese</th>
                <th className="py-1.5 px-2 text-center">Punteggio</th>
                <th className="py-1.5 pl-2 text-right">Punti</th>
              </tr></thead>
              <tbody>
                {(d.points_breakdown || []).map((r) => (
                  <tr key={r.machine} className="border-b hairline">
                    <td className="py-1.5 pr-2 t-body">{r.machine}</td>
                    <td className="py-1.5 px-2 text-center font-mono">{r.qty}</td>
                    <td className="py-1.5 px-2 text-center font-mono t-muted">{r.score}</td>
                    <td className="py-1.5 pl-2 text-right font-mono font-600 text-[#6b3fa0]">{r.points}</td>
                  </tr>
                ))}
                {(!d.points_breakdown || d.points_breakdown.length === 0) && (
                  <tr><td colSpan={4} className="py-4 text-center t-faint text-xs">Nessun macchinario da anagrafica prodotto questo mese</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] t-muted">Somma di (punteggio macchina × q.tà prodotta). Il punteggio di ogni macchina si imposta in Anagrafica Macchine (solo Direzione).</p>
        </div>
      )}
      {kind === "cycle" && (
        <div className="space-y-1">
          <StatRow label="Tempo ciclo reale (h/pz)" cur={c.cycle_actual_h} pm={pm.cycle_actual_h} py={py.cycle_actual_h} />
          <StatRow label="Tempo ciclo standard (h/pz)" cur={c.cycle_std_h} pm={pm.cycle_std_h} py={py.cycle_std_h} />
          <StatRow label="Ore lavorate" cur={c.worked_h} pm={pm.worked_h} py={py.worked_h} suf=" h" />
        </div>
      )}
      {kind === "scrap" && (
        <div className="space-y-2">
          <StatRow label="% scarti" cur={c.scrap_pct} pm={pm.scrap_pct} py={py.scrap_pct} suf="%" />
          <StatRow label="Pezzi scartati" cur={c.scrap_qty} pm={pm.scrap_qty} py={py.scrap_qty} />
          <StatRow label="Costo scarti" cur={c.scrap_cost} pm={pm.scrap_cost} py={py.scrap_cost} money />
          <div className="mt-2"><div className="lbl mb-1">Registrazioni del mese</div>
            {monthScraps.length === 0 ? <div className="text-[11px] t-faint">Nessuna.</div> : monthScraps.map((s) => (
              <div key={s.id} className="flex items-center justify-between text-[12px] py-1 border-b hairline">
                <span className="t-body">{(s.date || "").slice(0, 10)} · {s.product || s.line} · {s.qty} pz · {s.cause || "—"}</span>
                {canEdit && <button data-testid={`del-scrap-${s.id}`} onClick={() => onDelScrap(s.id)} className="text-[#9c1e2b] p-0.5"><Trash2 size={13} /></button>}
              </div>
            ))}
          </div>
        </div>
      )}
      {kind === "downtime" && (
        <div className="space-y-2">
          <StatRow label="Ore fermo totali" cur={c.downtime_h} pm={pm.downtime_h} py={py.downtime_h} suf=" h" />
          <StatRow label="Costo riparazioni" cur={c.downtime_cost} pm={pm.downtime_cost} py={py.downtime_cost} money />
          <div className="mt-1"><div className="lbl mb-1">Per causale</div>
            {c.stop_by_cause.length === 0 ? <div className="text-[11px] t-faint">Nessun fermo.</div> : c.stop_by_cause.map((s) => (
              <div key={s.cause} className="flex items-center justify-between text-[12px] py-1 border-b hairline"><span className="t-body">{s.cause}</span><span className="t-muted">{Math.round(s.minutes)} min · {s.count}× · {euro(s.cost)}</span></div>
            ))}
          </div>
          <div className="mt-2"><div className="lbl mb-1">Registrazioni del mese</div>
            {monthStops.length === 0 ? <div className="text-[11px] t-faint">Nessuna.</div> : monthStops.map((s) => (
              <div key={s.id} className="flex items-center justify-between text-[12px] py-1 border-b hairline">
                <span className="t-body">{(s.date || "").slice(0, 10)} · {s.machine} · {s.cause} · {Math.round(s.duration_min)} min{s.cost ? ` · ${euro(s.cost)}` : ""}</span>
                {canEdit && <button data-testid={`del-stop-${s.id}`} onClick={() => onDelStop(s.id)} className="text-[#9c1e2b] p-0.5"><Trash2 size={13} /></button>}
              </div>
            ))}
          </div>
        </div>
      )}
      {kind === "value" && (
        <div className="space-y-1">
          <StatRow label="Valore prodotto" cur={d.month_window.value} pm={pm.value} py={py.value} money />
          <StatRow label="Target mese" cur={d.targets.value} pm={0} py={0} money />
          <StatRow label="Proiezione fine mese" cur={d.projection.value} pm={0} py={0} money />
        </div>
      )}
    </Modal>
  );
}
